import {
    convertToDate,
    DEFAULT_DATE_FORMAT,
    DEFAULT_DATE_REGEX,
    format,
} from '@fruitful/fruitils';
import { CancellationReason } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { lastDayOfYear, startOfDay } from 'date-fns';
import { type Href, Link } from 'expo-router';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledCheckbox,
    ControlledDateTextInput,
    ControlledInput,
    ControlledMultilineInput,
    ControlledPicker,
    DateSelectionSheet,
    Form,
    H5Regular,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { toCancellationDateIsoString } from '../../common/utils/cancellationDateUtils';
import { stripeCustomerDashboardUrl } from '../../common/utils/LinkUtils';
import { type MembershipDetails } from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { useInitiateMembershipCancellation } from '../hooks/useInitiateMembershipCancellation';

const initiateMembershipCancellationSchema = z.object({
    reason: z.nativeEnum(CancellationReason),
    notes: z.string(),
    date: z.string().regex(DEFAULT_DATE_REGEX, 'Required field'),
    isImmediate: z.boolean(),
    confirm: z.string().regex(/CONFIRM/, 'Please type CONFIRM to confirm'),
});

type InitiateMembershipCancellationSchema = z.infer<
    typeof initiateMembershipCancellationSchema
>;

interface CancelMembershipModalProps {
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

/* We use manual indentation and disabled rows to create a visual hierarchy in the dropdown. */
const CANCELLATION_REASON_OPTIONS = [
    { label: 'None', value: CancellationReason.NONE },
    {
        label: 'No longer need guidance',
        value: CancellationReason.NO_LONGER_NEED_GUIDANCE,
    },
    { label: 'No time', value: CancellationReason.NO_TIME },
    { label: 'Too expensive', value: CancellationReason.TOO_EXPENSIVE },
    { label: 'Life change', value: CancellationReason.LIFE_CHANGE },
    {
        label: 'Does not meet expectations',
        value: CancellationReason.DOES_NOT_MEET_EXPECTATIONS,
    },
    {
        label: 'Does not want to adopt',
        value: CancellationReason.DOES_NOT_WANT_TO_ADOPT,
    },
    { label: 'Other', value: CancellationReason.OTHER },
    {
        label: 'Product: Onboarding or account linking',
        value: CancellationReason.PRODUCT_ONBOARDING_OR_ACCOUNT_LINKING,
    },
    {
        label: 'Product: Money system',
        value: CancellationReason.PRODUCT_MONEY_SYSTEM,
    },
    {
        label: 'Product: Banking issues',
        value: CancellationReason.PRODUCT_BANKING_ISSUES,
    },
    {
        label: 'Product: Investment management',
        value: CancellationReason.PRODUCT_INVESTMENT_MANGEMENT,
    },
    {
        label: 'Product: No Android App',
        value: CancellationReason.PRODUCT_NO_ANDROID_APP,
    },
    {
        label: 'Product: Other missing feature',
        value: CancellationReason.PRODUCT_OTHER_MISSING_FEATURE,
    },
    {
        label: 'Guidance: Switched advisors',
        value: CancellationReason.GUIDANCE_SWITCHED_ADVISORS,
    },
    {
        label: 'Guidance: No evening sessions',
        value: CancellationReason.GUIDANCE_NO_EVENING_SESSIONS,
    },
    {
        label: 'Guidance: Scheduling issues',
        value: CancellationReason.GUIDANCE_SCHEDULING_ISSUES,
    },
    {
        label: 'Financial: High DTI',
        value: CancellationReason.FINANCIAL_HIGH_DTI,
    },
    {
        label: 'Financial: Negative Cash Flow',
        value: CancellationReason.FINANCIAL_NEGATIVE_CASH_FLOW,
    },
    {
        label: 'Compliance: Not US based',
        value: CancellationReason.COMPLIANCE_NOT_US_BASED,
    },
    {
        label: 'Compliance: Conflicting interests',
        value: CancellationReason.COMPLIANCE_CONFLICTING_INTERESTS,
    },
    {
        label: 'Compliance: Emigrant initiated',
        value: CancellationReason.COMPLIANCE_EMIGRANT_INITIATED,
    },
    { label: 'Fraud: Suspected', value: CancellationReason.FRAUD_SUSPECTED },
    { label: 'Fraud: Confirmed', value: CancellationReason.FRAUD_CONFIRMED },
    { label: 'Fraud: Bot', value: CancellationReason.FRAUD_BOT },
    {
        label: 'Hospitality: Disengaged',
        value: CancellationReason.HOSPITALITY_DISENGAGED,
    },
    {
        label: 'Hospitality: Membership past due',
        value: CancellationReason.HOSPITALITY_MEMBERSHIP_PAST_DUE,
    },
    {
        label: 'Hospitality: Not hospitable',
        value: CancellationReason.HOSPITALITY_NOT_HOSPITABLE,
    },
    {
        label: 'Hospitality: Not comfortable with tech',
        value: CancellationReason.HOSPITALITY_NOT_COMFORTABLE_WITH_TECH,
    },
];

export const CancelMembershipModal = ({
    isOpen,
    onClose,
    membershipDetails,
}: CancelMembershipModalProps) => {
    const { id, users } = membershipDetails;
    const firstNames = users.map((user) => user.firstName).join(' and ');

    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessContent, setShowSuccessContent] = useState(false);
    const [isDateSelectionSheetOpen, setIsDateSelectionSheetOpen] =
        useState(false);

    const refetchOptions =
        useRefetchMembershipDetailsMutationOptions(membershipDetails);
    const { mutateAsync: initiateMembershipCancellation } =
        useInitiateMembershipCancellation(refetchOptions);

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<InitiateMembershipCancellationSchema>({
        resolver: zodResolver(initiateMembershipCancellationSchema),
        defaultValues: {
            reason: CancellationReason.NONE,
            notes: '',
            date: format(new Date(), DEFAULT_DATE_FORMAT),
            isImmediate: false,
            confirm: '',
        },
    });

    const dateValue = watch('date');

    // Reset isImmediate to false when date is not today
    useEffect(() => {
        if (dateValue !== new Date().toLocaleDateString()) {
            setValue('isImmediate', false);
        }
    }, [dateValue, setValue]);

    const onSubmit: SubmitHandler<
        InitiateMembershipCancellationSchema
    > = async ({ reason, notes, date, isImmediate }) => {
        setIsSubmitting(true);

        try {
            await initiateMembershipCancellation({
                urlParams: { id },
                body: {
                    cancellationReason: reason,
                    cancellationNotes: notes,
                    // Per HD, best practice is to execute cancellations at 11:59:59pm Pacific time.
                    cancellationDate: toCancellationDateIsoString(date),
                    isImmediate,
                },
            });

            toast.show(
                'Membership cancellation initiated',
                MutationSuccessToastOptions,
            );

            setShowSuccessContent(true);
        } catch (e) {
            toast.show('Error ending membership', MutationErrorToastOptions());
        } finally {
            setIsSubmitting(false);
        }
    };

    const maxEndDate = new Date();
    maxEndDate.setFullYear(maxEndDate.getFullYear() + 1);

    return (
        <>
            <GroveSimpleModal
                isOpen={isOpen}
                hasCloseButton={true}
                onClose={() => {
                    setShowSuccessContent(false);
                    onClose();
                }}
                renderInAppRoot={false}
            >
                {showSuccessContent ? (
                    // Post-success instructions content
                    <YStack
                        padding={24}
                        marginTop={32}
                        width={600}
                        gap={24}
                        marginBottom={24}
                    >
                        <H5Regular>Reminders:</H5Regular>
                        <YStack gap={4} paddingLeft={16}>
                            <BodySmallRegular>
                                1. Refund the membership in{' '}
                                <Link
                                    href={
                                        stripeCustomerDashboardUrl(
                                            membershipDetails.users.find(
                                                (user) =>
                                                    user.stripeCustomerId !=
                                                    null,
                                            )?.stripeCustomerId ?? '',
                                        ) as Href
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        textDecorationLine: 'underline',
                                    }}
                                >
                                    Stripe
                                </Link>{' '}
                                if applicable.
                            </BodySmallRegular>
                            <BodySmallRegular>
                                2. Close or flag any Investment accounts
                                according to the{' '}
                                <Link
                                    href="https://app.notion.com/p/fruitfulteam/Membership-Cancellation-guide-22587d94acdb819ea803f299c7b7632e?source=copy_link"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        textDecorationLine: 'underline',
                                    }}
                                >
                                    Cancellation Guide
                                </Link>
                                .
                            </BodySmallRegular>
                        </YStack>

                        <View alignSelf="center">
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                text="Got it"
                                onPress={onClose}
                            />
                        </View>
                    </YStack>
                ) : (
                    // Form content
                    <YStack
                        marginHorizontal={16}
                        marginTop={32}
                        width={500}
                        gap={24}
                        marginBottom={24}
                    >
                        <H5Regular>
                            Cancel Membership for {firstNames}
                        </H5Regular>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <YStack gap={20}>
                                <ControlledPicker
                                    label="Cancellation Reason"
                                    name="reason"
                                    size="tiny"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={CANCELLATION_REASON_OPTIONS}
                                />
                                <ControlledDateTextInput
                                    name="date"
                                    label="Date"
                                    size="tiny"
                                    fontWeight="regular"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    onPressIcon={() => {
                                        setIsDateSelectionSheetOpen(true);
                                    }}
                                />
                                {dateValue ===
                                    format(new Date(), DEFAULT_DATE_FORMAT) && (
                                    <ControlledCheckbox
                                        name="isImmediate"
                                        label="Cancel immediately"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                    />
                                )}
                                <ControlledMultilineInput
                                    name="notes"
                                    label="Notes"
                                    size="tiny"
                                    placeholder="Notes..."
                                    fontWeight="regular"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />
                                <ControlledInput
                                    name="confirm"
                                    label='Type "CONFIRM" to confirm'
                                    size="tiny"
                                    fontWeight="regular"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />
                                <View alignSelf="flex-start">
                                    <Button
                                        disabled={!isValid || isSubmitting}
                                        variant={ButtonVariant.PRIMARY}
                                        size={ButtonSize.TINY}
                                        tone="danger"
                                        text="Cancel membership"
                                        onPress={handleSubmit(onSubmit)}
                                    />
                                </View>
                            </YStack>
                        </Form>
                    </YStack>
                )}
            </GroveSimpleModal>
            <DateSelectionSheet
                title="Select cancellation date"
                isOpen={isDateSelectionSheetOpen}
                onClose={() => {
                    setIsDateSelectionSheetOpen(false);
                }}
                defaultValue={convertToDate(dateValue)}
                minimumDate={startOfDay(new Date())}
                maximumDate={lastDayOfYear(maxEndDate)}
                onConfirm={(date) => {
                    setValue('date', format(date, DEFAULT_DATE_FORMAT), {
                        shouldValidate: true,
                    });
                }}
                confirmButtonText="Set cancellation date"
                isInsideModal
            />
        </>
    );
};
